import { HIDDEN, PARTNER_RULE, POPULATION_RULE } from '@/constants/data_shares';
import { useDataSharesStore, usePartnersStore } from '@/stores';
import type { OutgoingSharingRule } from '@/types/data_shares';
import type { Partner } from '@/types/partners';

/**
 * Shared source of truth for whether a partner is on a population's default
 * sharing settings, used by both the Sharing Settings Hub and the Sharing Dashboard.
 */
export default function usePopulationDefaultUsage() {
  const dataSharesStore = useDataSharesStore();
  const partnersStore = usePartnersStore();

  function getDefaultSharingRule(
    populationId: number,
  ): OutgoingSharingRule | undefined {
    return dataSharesStore.getOutgoingSharingRules({
      ruleType: POPULATION_RULE,
      populationId,
    })[0];
  }

  function getPartnerOverrides(
    populationId: number,
  ): Record<number, OutgoingSharingRule> {
    const overridesByPartner: Record<number, OutgoingSharingRule> = {};
    const partnerRules = dataSharesStore.getOutgoingSharingRules({
      ruleType: PARTNER_RULE,
      populationId,
    });

    partnerRules.forEach((rule) => {
      if (rule.partner_org_id) overridesByPartner[rule.partner_org_id] = rule;
    });

    return overridesByPartner;
  }

  /**
   * Splits partners by whether their effective sharing rule matches the
   * population default. A partner-level rule that matches the default on both
   * visibility and field preset still counts as using the default.
   */
  function getPartnersUsingDefault(populationId: number): {
    partnersUsingDefaults: Partner[];
    partnersNotUsingDefaults: Partner[];
  } {
    const defaultRule = getDefaultSharingRule(populationId);
    const defaultVisibility = defaultRule?.visibility ?? HIDDEN;
    const defaultPresetId = defaultRule?.data_share_presets_id ?? null;

    const overridesByPartner = getPartnerOverrides(populationId);
    const getOverrideCount = dataSharesStore.outgoingOverrideCountLookup;

    const partnersUsingDefaults: Partner[] = [];
    const partnersNotUsingDefaults: Partner[] = [];

    const partners = partnersStore.partnerOrgs.filter(
      (partner) => !partner.offline_partner && !partner.virtual_partner,
    );

    for (const partner of partners) {
      const effectiveRule = overridesByPartner[partner.id] ?? defaultRule;

      const matchesDefaults =
        (effectiveRule?.visibility ?? HIDDEN) === defaultVisibility &&
        (effectiveRule?.data_share_presets_id ?? null) === defaultPresetId;

      const hasOverrides = getOverrideCount(populationId, partner.id) > 0;

      if (matchesDefaults && !hasOverrides) {
        partnersUsingDefaults.push(partner);
      } else {
        partnersNotUsingDefaults.push(partner);
      }
    }

    return { partnersUsingDefaults, partnersNotUsingDefaults };
  }

  return {
    getDefaultSharingRule,
    getPartnerOverrides,
    getPartnersUsingDefault,
  };
}
